import { useMemo, useState } from 'react';
import type { ChannelRole, Fee, FeeBasis, Programme, QuantityDef } from '@tariff/authoring';
import { Rich, useT, type MessageKey, type TFunction } from '../lib/i18n';
import { money } from '../lib/format';

/**
 * Who fits, who is counted, and what is charged on top.
 *
 * ---------------------------------------------------------------------------
 * THREE THINGS, ONE SCREEN, AND WHY
 * ---------------------------------------------------------------------------
 *
 * Guest categories, occupancy limits and extras are one screen because they are
 * one decision. "Two people are included, the third pays €5.50, children over
 * three count as people, one dog allowed" is a single sentence an operator says
 * about a pitch; splitting it across three tabs means holding it in your head
 * while you navigate.
 *
 * The part worth defending is the CHANNEL column. A park counts guests however
 * its price list needs — babies, kleuters, kinderen, jeugd, volwassenen — and
 * every OTA counts them exactly one way and will not change. That mapping has
 * to be stated somewhere, and stating it here, next to the categories
 * themselves, is the only place an operator can see both at once. Hiding it in
 * a channel-manager config is how parks end up distributing prices for the
 * wrong party.
 *
 * Nothing here is priced by the search. Fees are applied to the winning tiling
 * afterwards, so a tourist tax cannot be "avoided" by the engine choosing a
 * cheaper tiling — see `fees.ts`.
 */

/**
 * Both of these are closed sets from the schema, not operator text, so they are
 * ours to translate — the catalogue key travels with the value and the words
 * are looked up at the point of rendering.
 */
export const ROLES: Array<[ChannelRole, MessageKey]> = [
  ['adult', 'guests.roleAdult'],
  ['child', 'guests.roleChild'],
  ['infant', 'guests.roleInfant'],
  ['other', 'guests.roleOther'],
];

const BASES: Array<[FeeBasis, MessageKey]> = [
  ['perStay', 'guests.basisPerStay'],
  ['perNight', 'guests.basisPerNight'],
  ['perPerson', 'guests.basisPerPerson'],
  ['perPersonPerNight', 'guests.basisPerPersonPerNight'],
];

/** Channel role as stored, defaulted the way the compiler defaults it. */
function roleOf(q: QuantityDef): ChannelRole {
  if (q.channel) return q.channel;
  if (q.countsTowardOccupancy === false) return 'other';
  return 'adult';
}

/**
 * A fee's basis in words.
 *
 * The engine reports the basis as the stored value, and an unknown one is shown
 * as it arrived rather than hidden — a basis this screen has not been taught is
 * a fact worth seeing.
 */
function basisLabel(t: TFunction, basis: string): string {
  const known = BASES.find(([id]) => id === basis);
  return known ? t(known[1]) : basis;
}

function countsOf(q: QuantityDef): boolean {
  if (q.countsTowardOccupancy !== undefined) return q.countsTowardOccupancy;
  return roleOf(q) !== 'other';
}

function freshKey(existing: readonly QuantityDef[], base: string): string {
  if (!existing.some((q) => q.key === base)) return base;
  for (let n = 2; ; n++) {
    if (!existing.some((q) => q.key === `${base}${n}`)) return `${base}${n}`;
  }
}

export interface GuestsProps {
  programme: Programme;
  readOnly?: boolean;
  onChange: (next: Programme) => void;
}

export function Guests({ programme, readOnly, onChange }: GuestsProps) {
  const t = useT();
  const quantities = programme.quantities ?? [];
  const occupancy = programme.occupancy;
  const fees = programme.fees ?? [];
  const [preview, setPreview] = useState<Record<string, number>>({});

  const patch = (p: Partial<Programme>) => onChange({ ...programme, ...p });

  const setQuantity = (i: number, q: Partial<QuantityDef>) =>
    patch({ quantities: quantities.map((x, j) => (j === i ? { ...x, ...q } : x)) });

  const setFee = (i: number, f: Partial<Fee>) =>
    patch({ fees: fees.map((x, j) => (j === i ? { ...x, ...f } : x)) });

  /**
   * What a channel would be told about the party in the preview boxes.
   *
   * Shown live rather than on save, because the whole point of the channel
   * column is that its consequences are not obvious: three categories folding
   * into one number is correct and still surprising the first time.
   */
  const channelView = useMemo(() => {
    let adults = 0; let children = 0; let infants = 0; let people = 0; let pets = 0;
    for (const q of quantities) {
      const n = preview[q.key] ?? 0;
      if (n <= 0) continue;
      const role = roleOf(q);
      if (q.key === occupancy?.pets?.quantity) pets += n;
      if (countsOf(q) && q.key !== occupancy?.pets?.quantity) people += n;
      if (role === 'adult') adults += n;
      else if (role === 'child') children += n;
      else if (role === 'infant') infants += n;
    }
    let key = `${adults}A`;
    if (children) key += `${children}C`;
    if (infants) key += `${infants}I`;
    const overPeople = occupancy ? people > occupancy.max : false;
    const overPets = occupancy?.pets ? pets > occupancy.pets.max : false;
    const billable = occupancy ? Math.max(0, people - occupancy.included) : 0;
    return { key, people, pets, overPeople, overPets, billable };
  }, [preview, quantities, occupancy]);

  return (
    <div className="stack">
      <section className="panel">
        <div className="panel-hd">
          <h2>{t('guests.categoriesHeading')}</h2>
          <span className="grow" />
          <span className="note">{t('guests.categoriesNote')}</span>
        </div>
        <div className="panel-bd">
          <p className="note" style={{ marginBottom: 10 }}>
            {/* A party is translated to channel roles only after the price is
                worked out in this park's own categories, which is why several
                priced age bands collapsing to one channel role is harmless. */}
            <Rich id="guests.categoriesIntro" />
          </p>
          <div className="scroll-x">
            <table className="losTable">
              <thead>
                <tr>
                  <th>{t('guests.columnName')}</th>
                  <th className="r">{t('guests.columnAges')}</th>
                  <th>{t('guests.columnChannel')}</th>
                  <th>{t('guests.columnAlsoBills')}</th>
                  <th className="r">{t('guests.columnIncluded')}</th>
                  <th className="r">{t('guests.columnTakesPlace')}</th>
                  <th className="r">{t('guests.columnTestParty')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {quantities.map((q, i) => (
                  <tr key={q.key}>
                    <td>
                      <input
                        className="mid" type="text" value={q.label}
                        aria-label={t('guests.categoryName', { number: i + 1 })}
                        disabled={readOnly}
                        onChange={(e) => setQuantity(i, { label: e.target.value })}
                      />
                      <div className="meta mono">{q.key}</div>
                    </td>
                    <td className="r">
                      <div className="row tight">
                        <input
                          className="tiny" type="number" min={0} max={120}
                          value={q.ageFrom ?? ''}
                          aria-label={t('guests.categoryAgeFrom', { number: i + 1 })}
                          disabled={readOnly || roleOf(q) === 'other'}
                          onChange={(e) => setQuantity(i, {
                            ageFrom: e.target.value === '' ? undefined : Number(e.target.value),
                          })}
                        />
                        <span className="note">–</span>
                        <input
                          className="tiny" type="number" min={0} max={120}
                          value={q.ageUntil ?? ''}
                          aria-label={t('guests.categoryAgeUntil', { number: i + 1 })}
                          disabled={readOnly || roleOf(q) === 'other'}
                          onChange={(e) => setQuantity(i, {
                            ageUntil: e.target.value === '' ? undefined : Number(e.target.value),
                          })}
                        />
                      </div>
                    </td>
                    <td>
                      <select
                        className="mid" value={roleOf(q)}
                        aria-label={t('guests.categoryChannel', { number: i + 1 })}
                        disabled={readOnly}
                        onChange={(e) => setQuantity(i, { channel: e.target.value as ChannelRole })}
                      >
                        {ROLES.map(([id, label]) => (
                          <option key={id} value={id}>{t(label)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {/*
                        The other roles this category BILLS.

                        REPORTED: a family with a child got "ask us for a price"
                        on a pitch that takes children perfectly happily. This
                        park's own form has always asked for adults and children
                        from three and added them into ONE slot — which is what
                        the tariff can express and a channel party cannot, until
                        the card says so here.

                        Counted together with the category's own role, so
                        "included in the base: 2" means two guests BETWEEN them:
                        two adults, or an adult and a child.
                      */}
                      <select
                        multiple size={3} className="mid"
                        aria-label={t('guests.categoryAlsoBills', { number: i + 1 })}
                        disabled={readOnly}
                        value={(q.alsoCounts ?? []) as string[]}
                        onChange={(e) => setQuantity(i, {
                          alsoCounts: Array.from(e.target.selectedOptions)
                            .map((o) => o.value as ChannelRole),
                        })}
                      >
                        {ROLES.filter(([id]) => id !== roleOf(q)).map(([id, label]) => (
                          <option key={id} value={id}>{t(label)}</option>
                        ))}
                      </select>
                    </td>
                    <td className="r">
                      {/*
                        How many of these the base price already covers.

                        DEFECT (reported): this park's campsite sells a pitch
                        whose base price includes two guests, and charges per
                        guest beyond them. That is what `includedInBase` says
                        and what the ENGINE prices with — and it had no control
                        here at all, so the only way to set it was to write to
                        the database. Every camping quote charged from the first
                        guest and came out two guests too dear.

                        Not the same field as the occupancy block's "included"
                        below, which counts PEOPLE across every category and is
                        what a channel is told. They agree on a card with one
                        guest category and can differ on a card with two, so the
                        validator compares them rather than either one guessing.
                      */}
                      <input
                        className="tiny" type="number" min={0} max={20}
                        value={q.includedInBase ?? ''}
                        aria-label={t('guests.categoryIncluded', { number: i + 1 })}
                        disabled={readOnly}
                        onChange={(e) => setQuantity(i, {
                          includedInBase: e.target.value === ''
                            ? undefined : Number(e.target.value),
                        })}
                      />
                    </td>
                    <td className="r">
                      {/* The field that genuinely differs between parks with
                          the same price list: whether a baby consumes one of
                          the included places. */}
                      <input
                        type="checkbox" checked={countsOf(q)}
                        aria-label={t('guests.categoryCounts', { number: i + 1 })}
                        disabled={readOnly || roleOf(q) === 'other'}
                        onChange={(e) => setQuantity(i, { countsTowardOccupancy: e.target.checked })}
                      />
                    </td>
                    <td className="r">
                      <input
                        className="tiny" type="number" min={0} max={20}
                        value={preview[q.key] ?? 0}
                        aria-label={t('guests.categoryTestCount', { label: q.label })}
                        onChange={(e) => setPreview((p) => ({
                          ...p, [q.key]: Number(e.target.value) || 0,
                        }))}
                      />
                    </td>
                    <td className="r">
                      <button
                        type="button" className="btn" disabled={readOnly}
                        onClick={() => patch({ quantities: quantities.filter((_, j) => j !== i) })}
                      >
                        {t('guests.remove')}
                      </button>
                    </td>
                  </tr>
                ))}
                {quantities.length === 0 && (
                  <tr>
                    <td colSpan={6} className="note">{t('guests.noCategories')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="row" style={{ marginTop: 12 }}>
            <button
              type="button" className="btn" disabled={readOnly}
              onClick={() => patch({
                quantities: [...quantities, {
                  // The KEY is an identifier — it is what a fee points at — so
                  // it stays English whatever the interface is showing. The
                  // LABEL is the operator's to rename, and starts in theirs.
                  key: freshKey(quantities, 'guest'),
                  label: t('guests.newCategoryLabel'),
                  channel: 'adult',
                }],
              })}
            >
              {t('guests.addCategory')}
            </button>
            <span className="grow" />
            <span className="note" data-testid="channel-preview">
              {t('guests.channelSees')} <strong>{channelView.key}</strong>
              {channelView.pets > 0 && ` · ${t('guests.petCount', { count: channelView.pets })}`}
              {' · '}
              {t('guests.counted', { count: channelView.people })}
              {channelView.overPeople && <span className="chip bad">{t('guests.overTheLimit')}</span>}
              {channelView.overPets && <span className="chip bad">{t('guests.tooManyPets')}</span>}
            </span>
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-hd">
          {/* Occupancy is checked BEFORE pricing, not during it: a party over
              the limit is refused outright rather than priced. There is no
              cheapest way to fit six people into a hut that takes four, and
              reporting it as "no tariff covers this period" would send the
              operator to the rate card for a problem that is not there. */}
          <h2>{t('guests.occupancyHeading')}</h2>
        </div>
        <div className="panel-bd">
          {occupancy ? (
            <>
              <div className="controls">
                <div className="field">
                  <label htmlFor="occ-inc">{t('guests.included')}</label>
                  <input
                    id="occ-inc" className="tiny" type="number" min={0} max={30}
                    value={occupancy.included} disabled={readOnly}
                    onChange={(e) => patch({
                      occupancy: { ...occupancy, included: Number(e.target.value) || 0 },
                    })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="occ-max">{t('guests.hardLimit')}</label>
                  <input
                    id="occ-max" className="tiny" type="number" min={1} max={30}
                    value={occupancy.max} disabled={readOnly}
                    onChange={(e) => patch({
                      occupancy: { ...occupancy, max: Number(e.target.value) || 1 },
                    })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="occ-pet">{t('guests.petsAllowed')}</label>
                  <input
                    id="occ-pet" className="tiny" type="number" min={0} max={5}
                    value={occupancy.pets?.max ?? 0} disabled={readOnly}
                    onChange={(e) => {
                      const max = Number(e.target.value) || 0;
                      const quantity = occupancy.pets?.quantity
                        ?? quantities.find((q) => roleOf(q) === 'other')?.key;
                      patch({
                        occupancy: quantity
                          ? { ...occupancy, pets: { quantity, max } }
                          : { ...occupancy, pets: undefined },
                      });
                    }}
                  />
                </div>
              </div>
              {/* Only shown when included == max, so it is a whole message on
                  its own rather than a clause glued onto a paragraph — that
                  shape is the one translators cannot move. */}
              {occupancy.included === occupancy.max && (
                <p className="note" style={{ marginTop: 10 }}>
                  {t('guests.includedEqualsLimit')}
                </p>
              )}
            </>
          ) : (
            <div className="row">
              <span className="note">{t('guests.noLimit')}</span>
              <button
                type="button" className="btn" disabled={readOnly}
                onClick={() => patch({ occupancy: { included: 2, max: 6 } })}
              >
                {t('guests.setLimit')}
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="panel">
        <div className="panel-hd">
          <h2>{t('guests.feesHeading')}</h2>
          <span className="grow" />
          <span className="note">{t('guests.feeCount', { count: fees.length })}</span>
        </div>
        <div className="panel-bd">
          <div className="scroll-x">
            <table className="losTable">
              <thead>
                <tr>
                  <th>{t('guests.columnItem')}</th>
                  <th className="r">{t('guests.columnAmount')}</th>
                  <th>{t('guests.columnBasis')}</th>
                  <th>{t('guests.columnCounts')}</th>
                  <th className="r">{t('guests.columnBeyondIncluded')}</th>
                  <th className="r">{t('guests.columnOptional')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {fees.map((f, i) => (
                  <tr key={f.id}>
                    <td>
                      <input
                        className="mid" type="text" value={f.label}
                        aria-label={t('guests.feeName', { number: i + 1 })}
                        disabled={readOnly}
                        onChange={(e) => setFee(i, { label: e.target.value })}
                      />
                    </td>
                    <td className="r">
                      <input
                        className="tiny" type="number" min={0} step={0.5} value={f.amount}
                        aria-label={t('guests.feeAmount', { number: i + 1 })}
                        disabled={readOnly}
                        onChange={(e) => setFee(i, { amount: Number(e.target.value) || 0 })}
                      />
                    </td>
                    <td>
                      <select
                        className="mid" value={f.basis}
                        aria-label={t('guests.feeBasis', { number: i + 1 })}
                        disabled={readOnly}
                        onChange={(e) => setFee(i, { basis: e.target.value as FeeBasis })}
                      >
                        {BASES.map(([id, label]) => (
                          <option key={id} value={id}>{t(label)}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="mid" value={f.quantity ?? ''}
                        aria-label={t('guests.feeQuantity', { number: i + 1 })}
                        disabled={readOnly}
                        onChange={(e) => setFee(i, { quantity: e.target.value || undefined })}
                      >
                        <option value="">{t('guests.everyoneCounted')}</option>
                        {quantities.map((q) => (
                          <option key={q.key} value={q.key}>{q.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="r">
                      <input
                        type="checkbox" checked={Boolean(f.beyondIncluded)}
                        aria-label={t('guests.feeBeyondIncluded', { number: i + 1 })}
                        disabled={readOnly}
                        onChange={(e) => setFee(i, { beyondIncluded: e.target.checked || undefined })}
                      />
                    </td>
                    <td className="r">
                      <input
                        type="checkbox" checked={Boolean(f.optional)}
                        aria-label={t('guests.feeOptional', { number: i + 1 })}
                        disabled={readOnly}
                        onChange={(e) => setFee(i, { optional: e.target.checked || undefined })}
                      />
                    </td>
                    <td className="r">
                      <button
                        type="button" className="btn" disabled={readOnly}
                        onClick={() => patch({ fees: fees.filter((_, j) => j !== i) })}
                      >
                        {t('guests.remove')}
                      </button>
                    </td>
                  </tr>
                ))}
                {fees.length === 0 && (
                  <tr>
                    <td colSpan={7} className="note">{t('guests.noFees')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <button
              type="button" className="btn" disabled={readOnly}
              onClick={() => patch({
                fees: [...fees, {
                  id: `fee-${fees.length + 1}`,
                  // Data with a starting value, as above: the operator renames
                  // it, and it should not arrive in a language they do not use.
                  label: t('guests.newFeeLabel'),
                  basis: 'perPersonPerNight' as FeeBasis, amount: 5, beyondIncluded: true,
                }],
              })}
            >
              {t('guests.addFee')}
            </button>
            <span className="grow" />
            {occupancy && channelView.billable > 0 && (
              <span className="note">
                {t('guests.beyondIncludedCount', {
                  count: channelView.billable, included: occupancy.included,
                })}
              </span>
            )}
          </div>
          <p className="note" style={{ marginTop: 10 }}>
            <Rich id="guests.feesExplained" />
          </p>
        </div>
      </section>
    </div>
  );
}

/** The extras breakdown under a quote. */
export function Extras({ extras, extrasTotal, accommodationTotal }: {
  extras?: Array<{
    id: string; label: string; kind: string; basis: string;
    amount: number; units: number; total: number; optional: boolean;
  }>;
  extrasTotal?: number;
  accommodationTotal?: number;
}) {
  const t = useT();
  if (!extras || extras.length === 0) return null;
  return (
    <div className="extras">
      <table>
        <thead>
          <tr>
            <th>{t('guests.extrasOnTop')}</th>
            <th className="r">{t('guests.extrasRate')}</th>
            <th className="r">{t('guests.extrasUnits')}</th>
            <th className="r">{t('guests.extrasTotal')}</th>
          </tr>
        </thead>
        <tbody>
          {accommodationTotal !== undefined && (
            <tr className="sub">
              <td>{t('guests.extrasAccommodation')}</td><td /><td />
              <td className="r">{money(accommodationTotal)}</td>
            </tr>
          )}
          {extras.map((e) => (
            <tr key={e.id} className={e.optional ? 'opt' : ''}>
              <td>
                {e.label}
                {e.kind === 'tax' && <span className="chip">{t('guests.extrasTax')}</span>}
                {/* Quoted but not owed: a booking screen offers it, the total
                    does not assume it. */}
                {e.optional && <span className="chip">{t('guests.extrasOptional')}</span>}
                <div className="meta">{basisLabel(t, e.basis)}</div>
              </td>
              <td className="r">{money(e.amount)}</td>
              <td className="r">{e.units}</td>
              <td className="r">{money(e.total)}</td>
            </tr>
          ))}
          {extrasTotal !== undefined && (
            <tr className="sub">
              <td>{t('guests.extrasSubtotal')}</td><td /><td /><td className="r">{money(extrasTotal)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
